import { add, set } from 'date-fns';

import { AppSettings } from 'app/features/settings/models';
import { ApiVersion, minimalSupportedApiVersion, UserInfo, UserSession } from 'app/shared/models';

export const maxLengthRecentActivitiesInUserSession = 24;
export const isUserInfoSupported = (apiVersion: ApiVersion) => apiVersion >= minimalSupportedApiVersion;

export const isAuthorized = ({
    userInfo,
    requiredRoles,
    apiVersion,
}: {
    userInfo: UserInfo | undefined;
    requiredRoles: string[];
    apiVersion: ApiVersion;
}) => {
    if (!isUserInfoSupported(apiVersion) || !requiredRoles?.length) return true;

    return requiredRoles.every((requiredRole) => userInfo?.roles.includes(requiredRole));
};

export const getUserSessionExpirationTime = ({
    userSession,
    appSettings,
}: {
    userSession: UserSession;
    appSettings: AppSettings;
}): number => {
    const { hours, minutes, seconds, milliseconds } = appSettings.userSessionExpirationTime;
    const expirationTimeSameDate = set(new Date(userSession.startedOn), {
        hours,
        minutes,
        seconds,
        milliseconds,
    }).getTime();

    const expirationTimeNextDay = add(new Date(expirationTimeSameDate), { days: 1 }).getTime();
    return userSession.startedOn < expirationTimeSameDate ? expirationTimeSameDate : expirationTimeNextDay;
};

export const isUserSessionExpired = ({
    userSession,
    appSettings,
}: {
    userSession: UserSession;
    appSettings: AppSettings;
}) => {
    // supervisor session should never expire in 'multi-user' mode
    if (appSettings.appMode === 'multi-user') return false;

    const expirationTime = getUserSessionExpirationTime({ userSession, appSettings });
    const currentDate = Date.now();

    return currentDate > expirationTime;
};
